import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Modal,
  Space,
  Spin,
  Typography,
} from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';

import {
  PF,
  SELECTABLE_FIELDS,
  STAT_FIELDS,
} from '@app/components/Modals/CombineClientsModal/data/constants';
import {
  useCombineClientsModal,
  useCombineClientsModalEffects,
} from '@app/components/Modals/CombineClientsModal/hooks';
import type { CombineClientsModalProps } from '@app/components/Modals/CombineClientsModal/types';
import { getClientFieldValue } from '@app/components/Modals/CombineClientsModal/utils';

import type { ClientModel } from '@pages/DublicateClients/types';

import '@app/components/Modals/CombineClientsModal/styles/CombineClientsModal.desktop.scss';

const { Text, Title } = Typography;

export const CombineClientsModal = ({
  open,
  combineClients,
  onCancel,
  onSave,
  onMarkNotDuplicate,
}: CombineClientsModalProps) => {
  const {
    initialModal,
    localClients,
    selectedByField,
    handleSelectField,
    handleNotDuplicate,
    handleSaveClick,
    resultClient,
    modalWidth,
    handleSelectAllFromClient,
    tagOptions,
    isLoading,
  } = useCombineClientsModal({
    combineClients,
    onMarkNotDuplicate,
    onSave,
  });

  useCombineClientsModalEffects({ initialModal });

  const renderHeaderDate = (client: ClientModel) => {
    const dateLabel = `Создан ${moment(client.CreatedAt).format(
      'D MMMM YYYY'
    )}`;

    const isAllSelected = (clientId: number) =>
      Object.values(selectedByField).every((value) => value === clientId);

    return (
      <Space size={6} className={`${PF}__client-header`}>
        <Flex align="center" gap={8}>
          <Checkbox
            checked={isAllSelected(client.ClientId)}
            onChange={() => handleSelectAllFromClient(client.ClientId)}
          />
          <Text strong>{dateLabel}</Text>
        </Flex>
        <Link
          to={`/clients/edit/?id=${client.ClientId}&key=2`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </Link>
      </Space>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={modalWidth}
      title={
        <Title level={4} className={`${PF}__title`}>
          Объединение дубликатов клиентов ({combineClients?.Clients.length})
        </Title>
      }
      footer={
        <Flex gap={12}>
          <Button
            type="primary"
            size="large"
            className={`${PF}__button`}
            onClick={handleSaveClick}
            disabled={!resultClient || !localClients.length}
          >
            Сохранить
          </Button>

          <Button size="large" onClick={onCancel} className={`${PF}__button`}>
            Отменить
          </Button>
        </Flex>
      }
      destroyOnHidden
      className={`${PF}__modal`}
    >
      {' '}
      <Spin spinning={isLoading}>
        <div className={PF}>
          <div className={`${PF}__result-column`}>
            <Card
              size="small"
              className={`${PF}__column-card ${PF}__result-card`}
              title={<Text strong>Результат объединения</Text>}
            >
              {!!resultClient ? (
                <Space
                  direction="vertical"
                  size={8}
                  className={`${PF}__column-content`}
                >
                  {SELECTABLE_FIELDS.map(({ key, label }) => (
                    <div key={key} className={`${PF}__field-block`}>
                      <Text type="secondary" className={`${PF}__field-label`}>
                        {label}
                      </Text>

                      {key === 'Tags' ? (
                        <div className={`${PF}__tags-scroll`}>
                          {getClientFieldValue(resultClient, key, tagOptions)}
                        </div>
                      ) : (
                        <span className={`${PF}__field-value`}>
                          {getClientFieldValue(resultClient, key, tagOptions)}
                        </span>
                      )}
                    </div>
                  ))}

                  {STAT_FIELDS.map(({ key, label }) => (
                    <div key={key} className={`${PF}__field-block`}>
                      <Text type="secondary" className={`${PF}__field-label`}>
                        {label}
                      </Text>
                      <div className={`${PF}__field-value`}>
                        {resultClient[key]}
                      </div>
                    </div>
                  ))}
                  <div className={`${PF}__divider`} />
                </Space>
              ) : (
                <Text type="secondary">Нет данных для объединения</Text>
              )}
            </Card>
          </div>

          <div className={`${PF}__columns-wrapper`}>
            <div className={`${PF}__columns`}>
              {localClients.map((client) => (
                <Card
                  key={client.ClientId}
                  size="small"
                  className={`${PF}__column-card`}
                  title={renderHeaderDate(client)}
                >
                  <Space
                    direction="vertical"
                    size={8}
                    className={`${PF}__column-content`}
                  >
                    {SELECTABLE_FIELDS.map(({ key, label }) => (
                      <div key={key} className={`${PF}__field-block`}>
                        <Text type="secondary" className={`${PF}__field-label`}>
                          {label}
                        </Text>
                        <div
                          className={`${PF}__field-row ${
                            key === 'Tags' ? `${PF}__tags-scroll` : ''
                          }`}
                        >
                          <Checkbox
                            checked={selectedByField[key] === client.ClientId}
                            onChange={() =>
                              handleSelectField(key, client.ClientId)
                            }
                          >
                            <span className={`${PF}__checkbox-label`}>
                              {getClientFieldValue(client, key, tagOptions)}
                            </span>
                          </Checkbox>
                        </div>
                      </div>
                    ))}

                    {STAT_FIELDS.map(({ key, label }) => (
                      <div key={key} className={`${PF}__field-block`}>
                        <Text type="secondary" className={`${PF}__field-label`}>
                          {label}
                        </Text>
                        <div className={`${PF}__field-value`}>
                          {client[key]}
                        </div>
                      </div>
                    ))}

                    <div className={`${PF}__not-duplicate`}>
                      <Button
                        type="default"
                        size="small"
                        onClick={() => handleNotDuplicate(client.ClientId)}
                      >
                        Не дубликат
                      </Button>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
